import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Delete, ModeEdit } from '@mui/icons-material';
import { Box, CircularProgress, SpeedDialAction } from '@mui/material';
import { observer } from 'mobx-react-lite';
import {
  AccountLedger,
  AccountLedgerModal,
  AdvancesList,
  ConfirmModal,
  Documents,
  FuseTabs,
  LenderInformation,
  LenderOverview,
  Notes,
  ParticipationsList,
  PaymentsList,
  PortfolioSummary,
  ViewPage,
} from '/src/components';
import { useCompanyStore, useLenderStatementHeadlinesStore, useLenderStore } from '/src/context';
import { toastMessages } from '/src/lib/messages';
import { Lender, Note } from '/src/lib/models';
import { CRUDMode, DataModelType } from '/src/lib/types';
import { theme } from '/src/theme';

/**
 * View Lender Page
 */
export const ViewLenderPage = observer(() => {
  /** Context **/
  const companyStore = useCompanyStore();
  const lenderStore = useLenderStore();
  const statementHeadlinesStore = useLenderStatementHeadlinesStore();
  const params = useParams();
  const navigate = useNavigate();

  /** State **/
  const [showLedgerPopup, setShowLedgerPopup] = useState(false);
  const [showConfirmDeactivate, setShowConfirmDeactivate] = useState(false);

  const [lender, setLender] = useState<Lender>();
  const [notes] = useState<Note[]>([]);
  const [files] = useState<string[]>([]);

  /**
   * Array of booleans indicating which sidebar items are open.
   *
   * @remarks
   * Each item's index in the array corresponds to its position in the sidebar.
   */
  const [sidebarItems, setSidebarItems] = useState([
    true, // Investor Overview (open by default)
    false, // Portfolio Summary
    false, // Account Ledger
    false, // Lender Information
    false, // Notes
    false, // Documents
  ]);

  /** Methods **/

  /**
   * Deactivates the Lender.
   *
   * @remarks
   * We don't ever remove Lenders from the system, just mark them as "inactive".
   *
   * @remarks
   * Redirects to the Lenders List afterwards.
   */
  const deactivate = async () => {
    if (!lender) return;

    try {
      await lenderStore.deactivate(lender?._id);

      // Show alert
      toast.success(toastMessages.deleteLender.success);

      // Redirect to lenders
      navigate('/lenders');
    } catch (err) {
      console.error(err);
      toast.error(toastMessages.deleteLender.error);
    }
  };

  /**
   * Updates the Notes list.
   */
  const updateNotes = async (notes: Note[]) => {
    if (!lender) return;

    const response = await companyStore.update({ ...lender, notes });
    if (!response) throw new Error(`Error updating notes for Lender ID ${lender._id}`);

    lenderStore.currentLender = await lenderStore.get(lender._id);
  };

  /**
   * Updates the Documents list.
   *
   * @param files List of Document URLs.
   */
  const updateFiles = async (files: string[]) => {
    if (!lender) return;

    const response = await companyStore.update({ ...lender, files });
    if (!response) throw new Error(`Error updating files for Lender ID ${lender._id}`);

    lenderStore.currentLender = await lenderStore.get(lender._id);
  };

  /** Effects **/

  useEffect(() => {
    if (!params.lenderId) return;

    Promise.all([lenderStore.get(params.lenderId), statementHeadlinesStore.get({ lenderId: params.lenderId })]);
  }, [params.lenderId]);

  useEffect(() => {
    if (lenderStore.currentLender && statementHeadlinesStore.lenderStatementHeadlines) {
      const headlines = statementHeadlinesStore.lenderStatementHeadlines;
      setLender({ ...lenderStore.currentLender, alltime: headlines.alltime, ytd: headlines.ytd });
    }
  }, [lenderStore.currentLender, statementHeadlinesStore.lenderStatementHeadlines]);

  /** Render **/
  return (
    <ViewPage
      modelName={lender?.name ?? ''}
      modelType={DataModelType.Lender}
      sidebar={
        <>
          <LenderOverview
            lender={lender}
            open={sidebarItems[0]}
            toggleOpen={() => setSidebarItems(sidebarItems.with(0, !sidebarItems[0]))}
          />
          <PortfolioSummary
            lender={lender}
            open={sidebarItems[1]}
            toggleOpen={() => setSidebarItems(sidebarItems.with(1, !sidebarItems[1]))}
          />
          <AccountLedger
            lender={lender}
            open={sidebarItems[2]}
            toggleOpen={() => setSidebarItems(sidebarItems.with(2, !sidebarItems[2]))}
            setShowLedgerPopup={setShowLedgerPopup}
          />
          <LenderInformation
            lender={lender}
            open={sidebarItems[3]}
            toggleOpen={() => setSidebarItems(sidebarItems.with(3, !sidebarItems[3]))}
          />
          <Notes
            mode={CRUDMode.View}
            notes={notes ?? []}
            title="Notes"
            tooltip="Add Note"
            sidebarIndex={4}
            sidebarItems={sidebarItems}
            setSidebarItems={setSidebarItems}
            updateNotes={updateNotes}
          />
          <Documents
            mode={CRUDMode.Edit}
            documents={files ?? []}
            sidebarIndex={5}
            sidebarItems={sidebarItems}
            setSidebarItems={setSidebarItems}
            updateDocuments={updateFiles}
          />
        </>
      }
      speedDialActions={[
        <SpeedDialAction
          key="edit-button"
          className="md-fab-action-item"
          icon={<ModeEdit className="material-icons" />}
          slotProps={{
            fab: {
              'aria-label': 'Edit Investor',
              className: 'md-fab md-mini md-accent md-button md-default-theme',
            },
            tooltip: {
              title: 'Edit Investor',
            },
          }}
          onClick={() => navigate(`/companies/${lender?._id}/edit`)}
        />,
        <SpeedDialAction
          key="delete-button"
          className="md-fab-action-item md-warn"
          icon={<Delete className="material-icons" />}
          slotProps={{
            fab: {
              'aria-label': 'Deactivate Investor',
              className: 'md-fab md-mini md-warn md-button md-default-theme',
              style: { background: theme.palette.warning.main, color: 'white' },
            },
            tooltip: {
              title: 'Deactivate Investor',
            },
          }}
          onClick={() => setShowConfirmDeactivate(true)}
        />,
      ]}
      modals={
        <>
          <ConfirmModal
            content="Are you sure you want to deactivate this Investor? Doing so will deactivate its associated loans and assets as well."
            open={showConfirmDeactivate}
            setOpen={setShowConfirmDeactivate}
            onOk={() => deactivate()}
            label="Confirm Lender Deactivation"
            okLabel="Deactivate"
            cancelLabel="Cancel"
          />
          {lender && (
            <AccountLedgerModal
              lender={lender}
              setLender={setLender}
              open={showLedgerPopup}
              setOpen={setShowLedgerPopup}
            />
          )}
        </>
      }
    >
      {lender ? (
        <FuseTabs defaultTab={0}>
          {[
            {
              index: 0,
              label: 'Participations',
              content: lender ? <ParticipationsList data={lender.participations ?? []} /> : <></>,
            },
            {
              index: 1,
              label: 'Advances',
              content: lender ? <AdvancesList query={{ lenderId: lender._id }} /> : <></>,
            },
            {
              index: 2,
              label: 'Payments',
              content: lender ? <PaymentsList query={{ lenderId: lender._id }} /> : <></>,
            },
          ]}
        </FuseTabs>
      ) : (
        <Box display="flex" flex="100" justifyContent="center" alignItems="center" className="pad-top-bottom">
          <CircularProgress className="md-primary" md-mode="indeterminate" />
        </Box>
      )}
    </ViewPage>
  );
});
